import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { UserAddOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { observer } from "mobx-react-lite";

import { teamStore } from "../../stores/teamStore";
import { userStore } from "../../stores/userStore";
import { getVisibleApiKeyServices } from "../../utils/apiKeyServices";

import "./SidebarMenu.css";

const MENU_ITEMS = [
  { key: "profile", path: "/profile" },
  { key: "subscription", path: "/subscription" },
  { key: "webgis", path: "/webgis" },
  { key: "teams", path: "/teammanage" },
  { key: "software", path: "/software" },
  { key: "onpremise", path: "/myngidonpremises" },
  { key: "support", path: "/support" },
] as const;

const BOOK_CALL_URL = "https://cal.com/eduard.kazakov.nextgis/30min";

interface SidebarMenuProps {
  onNavigate?: () => void;
}

export const SidebarMenu = observer(({ onNavigate }: SidebarMenuProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const showBookCall = i18n.language.startsWith("en") && !userStore.isRuRegion;
  const hasApiKeyServices =
    getVisibleApiKeyServices(i18n.language, userStore.isRuRegion).length > 0;
  const incomingInvitationCount = teamStore.incomingInvitations.length;
  const goToIncomingInvitations = () => {
    navigate("/teammanage/member");
    onNavigate?.();
  };

  return (
    <nav className="sidebar-menu">
      {MENU_ITEMS.map(({ key, path }) => (
        <NavLink
          key={path}
          to={path}
          className={({ isActive }) =>
            `sidebar-menu__item${isActive ? " sidebar-menu__item--active" : ""}`
          }
          onClick={onNavigate}
        >
          <span>
            {t(
              key === "software" && hasApiKeyServices
                ? "software.combinedTitle"
                : `menu.${key}`,
            )}
          </span>
          {key === "teams" && incomingInvitationCount > 0 && (
            <span
              className="sidebar-menu__invitation-badge"
              aria-label={t("team.pendingInvitations")}
              role="link"
              tabIndex={0}
              title={t("team.pendingInvitations")}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToIncomingInvitations();
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  goToIncomingInvitations();
                }
              }}
            >
              <UserAddOutlined />
            </span>
          )}
        </NavLink>
      ))}
      {showBookCall && (
        <div className="sidebar-menu__book-call">
          <Tooltip
            title={t("menu.bookCallTooltip").replace(/\n/g, "\n")}
            placement="right"
          >
            <Button type="primary" href={BOOK_CALL_URL} target="_blank" block>
              {t("menu.bookCall")}
            </Button>
          </Tooltip>
        </div>
      )}
    </nav>
  );
});
